import { QuickOrder } from '@haus-storefront-react/quick-order'
import { type JSX, useId, useState } from 'react'
import { quickOrderVariantSkuValidationStrategy } from '@haus-storefront-react/vendure-plugin-configs/products-by-sku'
import { useTranslation } from 'react-i18next'
import { Button, Typography } from '@components'
import { PlusIcon, TrashIcon } from '@components/icons'
import type { QuickOrderAttributes } from './_propsFn'
import { ManualSkuField } from './ManualSkuField'
import styles from './QuickOrder.module.scss'
import inputStyles from '@components/input/Input.module.scss'

interface RowErrorPayload {
  submitError?: {
    message?: string
  }
  validationError?: {
    message?: string
  }
  message?: string
}

const DEFAULT_ROW_COUNT = 5
const isRowErrorPayload = (value: unknown): value is RowErrorPayload =>
  typeof value === 'object' && value !== null

/**
 * Render the Quick Order widget for manual multi-SKU add-to-cart.
 */
export const QuickOrderWidget = ({ configStyles }: QuickOrderAttributes): JSX.Element => {
  const { t } = useTranslation()
  const validationStrategy = quickOrderVariantSkuValidationStrategy()
  const idPrefix = useId()
  const [manualRootKey, setManualRootKey] = useState<number>(0)

  const submitButtonVariant = configStyles?.submitButtonVariant?.variant ?? 'primary'
  const clearButtonVariant = configStyles?.clearButtonVariant?.variant ?? 'outline'

  const errorMessage = (error: unknown): string => {
    if (!isRowErrorPayload(error)) {
      return t('general.genericErrorMessage')
    }

    const submitErrorMessage = error.submitError?.message
    if (submitErrorMessage === 'SKU_NOT_FOUND') {
      return t('quickOrder.manualInvalidSku')
    }
    if (submitErrorMessage === 'INSUFFICIENT_STOCK_ERROR') {
      // TODO: add quantityAvailable to translation key
      return t('quickOrder.outOfStock')
    }
    if (submitErrorMessage === 'ORDER_LIMIT_ERROR') {
      return t('quickOrder.orderLimit')
    }
    if (submitErrorMessage) {
      return submitErrorMessage
    }

    if (error.validationError?.message) {
      return error.validationError.message
    }

    return error.message || t('general.genericErrorMessage')
  }

  const resetRows = () => {
    setManualRootKey((currentKey) => currentKey + 1)
  }

  return (
    <>
      <QuickOrder.Root>
        {({ isLoading }) => (
          <div className={styles.form}>
            <div
              id={`${idPrefix}-quick-order-manual-panel`}
              role="tabpanel"
              aria-labelledby={`${idPrefix}-quick-order-tab-manual`}
              className={styles.manualPanel}
            >
              <QuickOrder.Manual.Root
                key={manualRootKey}
                validationStrategy={validationStrategy}
                initialRows={DEFAULT_ROW_COUNT}
              >
                {({
                  rows,
                  getValidRows,
                  updateRow,
                  addRow,
                  errors,
                  hasErrors,
                  hasSuccess,
                  successCount,
                }) => {
                  const validRows = getValidRows()
                  const validCount = validRows.length

                  return (
                    <>
                      {hasSuccess && (
                        <div className={styles.resultMessageSuccess} role="alert">
                          <Typography variant="body" as="span">
                            {t('quickOrder.formSuccessMessage', { successCount: successCount })}
                          </Typography>
                        </div>
                      )}
                      {hasErrors && (
                        <div className={styles.resultMessageError} role="alert">
                          <Typography variant="body" as="span">
                            {t('quickOrder.formErrorsMessage', { failedItemCount: errors.length })}
                          </Typography>
                        </div>
                      )}

                      <div className={styles.formHeader}>
                        <span className={styles.columnHeaderSku}>{t('quickOrder.columnSku')}</span>
                        <span className={styles.columnHeaderSku}>
                          {t('quickOrder.columnQuantity')}
                        </span>
                      </div>

                      <div className={styles.formRows}>
                        {rows.map((row, rowIndex) => (
                          <QuickOrder.Manual.Row key={row.id} row={row}>
                            {({ row, error }) => {
                              return (
                                <div className={styles.formRow}>
                                  <div className={styles.formRowGrid}>
                                    <div className={styles.inputCellSku}>
                                      <span
                                        className={`${styles.columnHeaderSku} ${styles.mobileOnlyHeader}`}
                                      >
                                        {t('quickOrder.columnSku')}
                                      </span>
                                      <ManualSkuField
                                        row={row}
                                        inputAriaLabel={`${t('quickOrder.identifierLabel')} ${rowIndex + 1}`}
                                        hasError={Boolean(error)}
                                        searchingLabel={t('quickOrder.manualSearching')}
                                        noResultsLabel={t('quickOrder.manualNoSuggestions')}
                                        onInputChange={(rowId: string, value: string) => {
                                          updateRow(rowId, 'identifier', value)
                                        }}
                                      />
                                    </div>
                                    <div className={styles.inputCellActions}>
                                      <div className={styles.quantityInputGroup}>
                                        <label
                                          htmlFor={`${idPrefix}-quick-order-quantity-${row.id}`}
                                          className={`${styles.columnHeaderSku} ${styles.mobileOnlyHeader}`}
                                        >
                                          {t('quickOrder.columnQuantity')}
                                        </label>
                                        <QuickOrder.Manual.Row.QuantityInput
                                          id={`${idPrefix}-quick-order-quantity-${row.id}`}
                                          type="number"
                                          min={1}
                                          aria-label={t('quickOrder.quantityLabel')}
                                          className={inputStyles.input}
                                        />
                                      </div>
                                      <QuickOrder.Manual.Row.RemoveButton
                                        title={t('quickOrder.removeRow')}
                                        className={styles.removeButton}
                                        aria-label={t('quickOrder.removeRow')}
                                      >
                                        <TrashIcon width={16} height={16} aria-hidden />
                                      </QuickOrder.Manual.Row.RemoveButton>
                                    </div>
                                  </div>
                                  {error && (
                                    <Typography
                                      variant="body"
                                      as="span"
                                      className={styles.manualRowError}
                                      role="alert"
                                    >
                                      {errorMessage(error)}
                                    </Typography>
                                  )}
                                </div>
                              )
                            }}
                          </QuickOrder.Manual.Row>
                        ))}
                      </div>

                      <button
                        type="button"
                        className={styles.addRowButton}
                        onClick={() => {
                          for (let i = 0; i < DEFAULT_ROW_COUNT; i += 1) {
                            addRow()
                          }
                        }}
                      >
                        <PlusIcon width={18} height={18} aria-hidden />
                        {t('quickOrder.addAnotherItems', {
                          defaultValue: 'Add {{rowCount}} More Items',
                          rowCount: DEFAULT_ROW_COUNT,
                        })}
                      </button>

                      <div className={styles.formActions}>
                        <QuickOrder.Manual.SubmitButton onClick={() => {}} asChild>
                          <Button
                            variant={submitButtonVariant}
                            className={styles.submitButton}
                            disabled={isLoading}
                          >
                            {t('quickOrder.addItemsToCart', {
                              variantCount: validCount,
                            })}
                          </Button>
                        </QuickOrder.Manual.SubmitButton>

                        <Button
                          type="button"
                          variant={clearButtonVariant as 'outline'}
                          className={styles.clearButton}
                          onClick={resetRows}
                          disabled={isLoading}
                        >
                          {t('quickOrder.clearForm')}
                        </Button>
                      </div>
                    </>
                  )
                }}
              </QuickOrder.Manual.Root>
            </div>
          </div>
        )}
      </QuickOrder.Root>
    </>
  )
}
